import React, { useState } from 'react';
import { RiPlayFill, RiFireFill, RiHeadphoneLine, RiShieldCheckLine, RiFlagLine, RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';
import styles from './HabitHero.module.css';
import AutoBookCover, { splitTitleAndAuthor } from './AutoBookCover';

const GOAL_PRESETS = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hr' },
  { minutes: 120, label: '2 hr' },
  { minutes: 300, label: '5 hr' },
];

const RING_RADIUS = 40;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Finish-line forecast guardrails (mirror habit_service.py): with less
// history than this the estimate is noise, so we show nothing.
const MIN_PACE_SECONDS = 120;
const MIN_ACTIVE_DAYS_LAST_14 = 2;
const MIN_REMAINING_SECONDS = 60;
// Beyond this horizon the date is demotivating noise; past this many
// sessions the count stops being meaningful, so show the date alone.
const MAX_HORIZON_DAYS = 60;
const MAX_SESSIONS_IN_COPY = 12;

// "At your pace you'll finish by <date>, ~N sessions" for the continue book.
// A session = one listening day at the user's average pace; sessions are
// spread over calendar days at their observed days-per-week cadence.
function computeForecast(insights, continueBook, bookPercent) {
  const pace = Number(insights?.paceSecondsPerActiveDay || 0);
  const activeDays14 = Number(insights?.activeDaysLast14 || 0);
  const totalDuration = Number(continueBook?.totalDuration || 0);
  if (!continueBook || totalDuration <= 0) return null;
  if (pace < MIN_PACE_SECONDS || activeDays14 < MIN_ACTIVE_DAYS_LAST_14) return null;

  const remaining = totalDuration * (1 - bookPercent / 100);
  if (remaining < MIN_REMAINING_SECONDS) return null;

  const sessions = Math.max(1, Math.ceil(remaining / pace));
  const activePerWeek = Math.min(7, Math.max(1, Math.round(activeDays14 / 2)));
  const days = Math.ceil((sessions * 7) / activePerWeek);
  if (days > MAX_HORIZON_DAYS) return null;
  const date = new Date();
  date.setDate(date.getDate() + days);

  const dateLabel = days <= 1
    ? 'tomorrow'
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return { sessions: sessions <= MAX_SESSIONS_IN_COPY ? sessions : null, dateLabel };
}

function HabitHero({
  loading,
  insights,
  continueBook,
  continueTitle,
  isOpening,
  onResume,
  onStartBrowsing,
  onSaveGoal,
  // Compact: a one-line strip (continue + goal ring + streak) that expands
  // into the full card on tap. The Story Time hero above it has the space now.
  compact = false,
}) {
  const [editingGoal, setEditingGoal] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const showCompact = compact && !expanded;

  if (loading) {
    if (compact) {
      return (
        <section className={`${styles.hero} ${styles.heroCompact}`} aria-busy="true" aria-label="Loading your listening summary">
          <div className={styles.skeletonSlot}>
            <div className={styles.skeletonCoverSmall} />
            <div className={styles.skeletonLines}>
              <div className={styles.skeletonLine} style={{ width: '55%' }} />
            </div>
          </div>
        </section>
      );
    }
    return (
      <section className={styles.hero} aria-busy="true" aria-label="Loading your listening summary">
        <div className={styles.skeletonSlot}>
          <div className={styles.skeletonCover} />
          <div className={styles.skeletonLines}>
            <div className={styles.skeletonLine} style={{ width: '40%' }} />
            <div className={styles.skeletonLine} style={{ width: '70%' }} />
            <div className={styles.skeletonLine} style={{ width: '30%' }} />
          </div>
        </div>
        <div className={styles.skeletonRing} />
      </section>
    );
  }

  const goal = Number(insights?.goalWeeklyMinutes || 0);
  const done = Number(insights?.listenedThisWeekMinutes || 0);
  const streak = Number(insights?.currentStreakDays || 0);
  const graceUsed = Boolean(insights?.graceDayUsedThisWeek);
  const pct = goal > 0 ? Math.min(1, done / goal) : 0;
  const showPicker = editingGoal || goal <= 0;
  const bookPercent = Math.max(0, Math.min(100, Number(continueBook?.playbackPercent || 0)));
  const forecast = computeForecast(insights, continueBook, bookPercent);

  const handlePreset = async (minutes) => {
    setSavingGoal(true);
    try {
      await onSaveGoal(minutes);
      setEditingGoal(false);
    } finally {
      setSavingGoal(false);
    }
  };

  if (showCompact) {
    const continueName = splitTitleAndAuthor(continueTitle).title;
    return (
      <section className={`${styles.hero} ${styles.heroCompact}`} data-tour="habit-hero">
        {continueBook ? (
          <div
            className={styles.stripContinue}
            role="button"
            tabIndex={0}
            onClick={onResume}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onResume();
              }
            }}
            aria-label={`Continue listening to ${continueName}`}
          >
            {continueBook.coverUrl ? (
              <img className={styles.stripCover} src={continueBook.coverUrl} alt="" loading="lazy" />
            ) : (
              <div className={styles.stripCoverAuto} aria-hidden="true">
                <AutoBookCover variant="compact" title={continueName} seed={continueName} />
              </div>
            )}
            <div className={styles.stripBody}>
              <span className={styles.eyebrow}>Continue listening</span>
              <span className={styles.stripTitle}>{continueName}</span>
              <div className={styles.stripTrack} aria-hidden="true">
                <div className={styles.progressFill} style={{ width: `${bookPercent}%` }} />
              </div>
            </div>
            <button
              type="button"
              className={`${styles.resumeButton} ${styles.stripResume}`}
              disabled={isOpening}
              onClick={(event) => {
                event.stopPropagation();
                onResume();
              }}
              aria-label={isOpening ? 'Opening' : `Resume ${continueName}`}
            >
              {isOpening ? <span className={styles.spinner} aria-hidden="true" /> : <RiPlayFill size={18} />}
              <span className={styles.stripResumeLabel}>{isOpening ? 'Opening…' : 'Resume'}</span>
            </button>
          </div>
        ) : (
          <div className={styles.stripContinue}>
            <div className={styles.stripCoverFallback} aria-hidden="true">
              <RiHeadphoneLine size={18} />
            </div>
            <div className={styles.stripBody}>
              <span className={styles.eyebrow}>Start a listening habit</span>
              <span className={styles.stripTitle}>Pick a story below and press play</span>
            </div>
          </div>
        )}

        {insights && (
          <div className={styles.stripStats}>
            <button
              type="button"
              className={styles.stripChip}
              onClick={() => setExpanded(true)}
              title={goal > 0 ? `${done} of ${goal} minutes this week` : 'Set a weekly goal'}
            >
              <span className={styles.stripRingWrap} aria-hidden="true">
                <svg viewBox="0 0 96 96" className={styles.stripRing}>
                  <circle cx="48" cy="48" r={RING_RADIUS} className={styles.ringTrack} />
                  {goal > 0 && (
                    <circle
                      cx="48"
                      cy="48"
                      r={RING_RADIUS}
                      className={styles.ringProgress}
                      strokeDasharray={RING_CIRCUMFERENCE}
                      strokeDashoffset={RING_CIRCUMFERENCE * (1 - pct)}
                      transform="rotate(-90 48 48)"
                    />
                  )}
                </svg>
              </span>
              {goal > 0 ? `${Math.round(pct * 100)}% of goal` : 'Set a goal'}
            </button>
            <span className={`${styles.stripChip} ${streak > 0 ? styles.streakActive : ''}`}>
              <RiFireFill size={14} aria-hidden="true" />
              {streak > 0 ? `${streak}-day streak` : 'No streak yet'}
            </span>
            <button
              type="button"
              className={styles.stripExpand}
              onClick={() => setExpanded(true)}
              aria-label="Show weekly goal and streak details"
            >
              <RiArrowDownSLine size={18} />
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={styles.hero} data-tour="habit-hero">
      {compact && (
        <button
          type="button"
          className={styles.collapseButton}
          onClick={() => setExpanded(false)}
          aria-label="Collapse listening summary"
        >
          <RiArrowUpSLine size={18} />
        </button>
      )}
      {continueBook ? (
        <div
          className={styles.continueSlot}
          role="button"
          tabIndex={0}
          onClick={onResume}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onResume();
            }
          }}
          aria-label={`Continue listening to ${continueTitle}`}
        >
          {continueBook.coverUrl ? (
            <img className={styles.cover} src={continueBook.coverUrl} alt="" loading="lazy" />
          ) : (
            <div className={styles.coverAutoWrap} aria-hidden="true">
              <AutoBookCover
                variant="compact"
                title={splitTitleAndAuthor(continueTitle).title}
                seed={splitTitleAndAuthor(continueTitle).title}
              />
            </div>
          )}
          <div className={styles.continueBody}>
            <span className={styles.eyebrow}>Continue listening</span>
            <h3 className={styles.title}>{splitTitleAndAuthor(continueTitle).title}</h3>
            <div className={styles.progressTrack} aria-hidden="true">
              <div className={styles.progressFill} style={{ width: `${bookPercent}%` }} />
            </div>
            <span className={styles.progressLabel}>{Math.round(bookPercent)}% finished</span>
            {forecast && (
              <span className={styles.forecast}>
                <RiFlagLine size={13} aria-hidden="true" />
                On pace to finish by <strong>{forecast.dateLabel}</strong>
                {forecast.sessions !== null && (
                  <>
                    {' · '}
                    {forecast.sessions === 1 ? 'one more session' : `~${forecast.sessions} sessions to go`}
                  </>
                )}
              </span>
            )}
            <button
              type="button"
              className={styles.resumeButton}
              disabled={isOpening}
              onClick={(event) => {
                event.stopPropagation();
                onResume();
              }}
            >
              {isOpening ? <span className={styles.spinner} aria-hidden="true" /> : <RiPlayFill size={18} />}
              {isOpening ? 'Opening…' : 'Resume'}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.continueSlot}>
          <div className={styles.coverFallback} aria-hidden="true">
            <RiHeadphoneLine size={28} />
          </div>
          <div className={styles.continueBody}>
            <span className={styles.eyebrow}>Start a listening habit</span>
            <h3 className={styles.title}>Pick a book and press play</h3>
            <p className={styles.emptyCopy}>
              Browse the shelves below or turn one of your own PDFs into an audiobook.
            </p>
            <button type="button" className={styles.resumeButton} onClick={onStartBrowsing}>
              New Book
            </button>
          </div>
        </div>
      )}

      {insights && (
        <div className={styles.statsSlot}>
          <div className={styles.ringWrap}>
            <svg viewBox="0 0 96 96" className={styles.ring}>
              <circle cx="48" cy="48" r={RING_RADIUS} className={styles.ringTrack} />
              {goal > 0 && (
                <circle
                  cx="48"
                  cy="48"
                  r={RING_RADIUS}
                  className={styles.ringProgress}
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={RING_CIRCUMFERENCE * (1 - pct)}
                  transform="rotate(-90 48 48)"
                />
              )}
            </svg>
            <div className={styles.ringCenter}>
              <strong>{goal > 0 ? `${Math.round(pct * 100)}%` : '—'}</strong>
              <span>this week</span>
            </div>
          </div>

          <div className={styles.statsBody}>
            {showPicker ? (
              <>
                <span className={styles.statsEyebrow}>Weekly goal</span>
                <div className={styles.goalPresets}>
                  {GOAL_PRESETS.map((preset) => (
                    <button
                      key={preset.minutes}
                      type="button"
                      className={`${styles.goalPreset} ${goal === preset.minutes ? styles.goalPresetActive : ''}`}
                      onClick={() => handlePreset(preset.minutes)}
                      disabled={savingGoal}
                    >
                      {preset.label}
                    </button>
                  ))}
                  {editingGoal && (
                    <button
                      type="button"
                      className={styles.goalCancel}
                      onClick={() => setEditingGoal(false)}
                      disabled={savingGoal}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className={styles.statsEyebrow}>Weekly goal</span>
                <span className={styles.goalSummary}>{done} / {goal} min</span>
                <button type="button" className={styles.goalEdit} onClick={() => setEditingGoal(true)}>
                  Change
                </button>
              </>
            )}
            <div className={`${styles.streakChip} ${streak > 0 ? styles.streakActive : ''}`}>
              <RiFireFill size={16} aria-hidden="true" />
              {streak > 0
                ? `${streak}-day streak`
                : 'Listen today to start a streak'}
            </div>
            {streak > 0 && graceUsed && (
              <div
                className={styles.graceChip}
                title="You missed one day this week, but your free weekly grace day kept the streak alive."
              >
                <RiShieldCheckLine size={13} aria-hidden="true" />
                grace day used
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default HabitHero;
